// Per-org user-configurable "outcome" columns for the triage board.
// Workflow authors attach a column to specific edges in the graph; when
// the runner traverses a tagged edge, the resulting Run carries the same
// `boardColumnId` so it lands in that column on the triage board's
// outcome view. Distinct from RunStatus (the workflow's execution state).
// (DEV-16183.)
import { z } from 'zod';
/**
 * Accent color tokens that mirror the kit's `fk-kanban-column__header--*`
 * variants. Restricted to a closed set so the UI can compile-time map
 * to known CSS classes without dynamic class construction.
 *
 * `null` (omitted) means "no accent" — the column header renders in the
 * default neutral surface; the edge chip in the builder falls back to
 * `var(--border-strong)`.
 */
export const BoardColumnColorSchema = z.enum(['teal', 'iris', 'amber', 'rose', 'mint', 'lavender']);
export const BoardColumnSchema = z.object({
    id: z.string(),
    orgId: z.string(),
    name: z.string(),
    /** Display order on the triage board (left-to-right ascending). */
    order: z.number().int(),
    color: BoardColumnColorSchema.nullable(),
    description: z.string().nullable(),
    createdAt: z.date(),
    updatedAt: z.date(),
    deletedAt: z.date().nullable(),
});
export const CreateBoardColumnInputSchema = z.object({
    name: z.string().min(1).max(80),
    /**
     * Optional — if omitted, server appends at the end of the org's list.
     * Callers building reorder UIs may set this explicitly.
     */
    order: z.number().int().min(0).optional(),
    color: BoardColumnColorSchema.nullable().optional(),
    description: z.string().max(400).nullable().optional(),
});
export const UpdateBoardColumnInputSchema = z.object({
    name: z.string().min(1).max(80).optional(),
    order: z.number().int().min(0).optional(),
    color: BoardColumnColorSchema.nullable().optional(),
    description: z.string().max(400).nullable().optional(),
});
/**
 * Bulk reorder primitive: client sends the desired left-to-right order
 * as a list of ids; server rewrites `order` on each row to match.
 * Avoids N PATCH round-trips when the admin drag-reorders columns.
 */
export const ReorderBoardColumnsInputSchema = z.object({
    ids: z.array(z.string()).min(1),
});
//# sourceMappingURL=board-column.js.map